import { Button } from '@/components/ui/Button';
import { ObjectiveCard, type ObjectiveView } from '@/components/profile/goals/ObjectiveCard';
import { OBJECTIVE_PRIORITIES, type ObjectiveProposal } from '@shared/types/objective-proposals';
import type { WorkspaceObjective } from '@shared/types/workspace-objectives';
import type { TenantMember } from '@shared/types/teams';

interface Props {
  objectives: WorkspaceObjective[];
  proposals: ObjectiveProposal[];
  members: TenantMember[];
  busy: boolean;
  onApply: (ids: string[]) => void;
  onDismiss: (ids: string[]) => void;
}

/** The main objective a view is a lever of (its key), or null. */
const parentOf = (view: ObjectiveView): string | null =>
  view.proposal && view.proposal.action !== 'archive' ? view.proposal.parentId ?? null : view.current?.parent_objective_id ?? null;

const priorityOf = (view: ObjectiveView) =>
  view.proposal && view.proposal.action !== 'archive' ? view.proposal.priority : view.current?.priority ?? 'normal';

/**
 * The company's objectives as they work together (13/10/2026): each main
 * objective, the most critical first, with the levers that produce it under it.
 * The analyst's pending proposals sit in place, each written once validated.
 */
export const ObjectivesBoard = ({ objectives, proposals, members, busy, onApply, onDismiss }: Props) => {
  const owner = (userId: string | null) => (userId ? members.find((member) => member.user_id === userId)?.display_name ?? 'Une personne' : 'Entreprise');
  // The analyst's order is the funnel's order: kept among levers.
  const views: ObjectiveView[] = [
    ...objectives.filter((objective) => objective.status === 'active').map((current) => ({
      key: current.id, current, proposal: proposals.find((proposal) => proposal.objectiveId === current.id) ?? null,
    })),
    ...proposals.filter((proposal) => proposal.action === 'create').map((proposal) => ({ key: proposal.id, current: null, proposal })),
  ];
  if (views.length === 0) return null;
  const keys = new Set(views.map((view) => view.key));
  // A lever whose main objective is not shown (dismissed, archived) stands on its own.
  const mains = views.filter((view) => !parentOf(view) || !keys.has(parentOf(view)!))
    .sort((a, b) => OBJECTIVE_PRIORITIES.indexOf(priorityOf(a)) - OBJECTIVE_PRIORITIES.indexOf(priorityOf(b)));
  const leversOf = (main: ObjectiveView) => views.filter((view) => parentOf(view) === main.key);
  const pending = proposals.map((proposal) => proposal.id);
  const pendingMain = new Set(proposals.filter((proposal) => proposal.action === 'create').map((proposal) => proposal.id));
  const card = (view: ObjectiveView, lead: boolean) => {
    const parent = parentOf(view);
    // A lever of a main objective still proposed is validated with it, or after it.
    const waitsFor = view.proposal && parent && pendingMain.has(parent) ? views.find((entry) => entry.key === parent)?.proposal?.title ?? null : null;
    return (
      <ObjectiveCard
        key={view.key}
        view={view}
        owner={owner(view.proposal && view.proposal.action !== 'archive' ? view.proposal.ownerUserId : view.current?.owner_user_id ?? null)}
        lead={lead}
        busy={busy}
        waitsFor={waitsFor}
        onApply={() => view.proposal && onApply([view.proposal.id])}
        onDismiss={() => view.proposal && onDismiss([view.proposal.id])}
      />
    );
  };

  return (
    <div className="min-w-0 space-y-8">
      {pending.length > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl bg-domino-lilac/60 px-4 py-3">
          <p className="text-sm text-domino-ink">{pending.length} proposition{pending.length > 1 ? 's' : ''} à valider</p>
          <Button size="sm" onClick={() => onApply(pending)} disabled={busy}>Tout valider</Button>
        </div>
      )}
      {mains.map((main) => {
        const levers = leversOf(main);
        return (
          <section key={main.key} aria-label={main.proposal?.title ?? main.current?.title ?? 'Objectif'} className="space-y-3">
            {card(main, true)}
            {levers.length > 0 && (
              <div className="ml-4 space-y-3 border-l-2 border-domino-cardBorder pl-4 sm:ml-6 sm:pl-6">
                <p className="text-xs font-semibold uppercase tracking-wide text-domino-inkSoft">Leviers</p>
                {levers.map((lever) => card(lever, false))}
              </div>
            )}
          </section>
        );
      })}
    </div>
  );
};
